import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InnerPage from '../components/InnerPage.jsx';
import {
  CONTACT_TOPICS,
  isValidContactTopic,
} from '../data/contactTopics.js';

function randomCaptcha() {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 5; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function FieldIcon({ children, top = false }) {
  return (
    <span className={`contact-form__icon${top ? ' contact-form__icon--top' : ''}`} aria-hidden="true">
      {children}
    </span>
  );
}

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const topicFromUrl = searchParams.get('topic') || '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [topic, setTopic] = useState(() => (isValidContactTopic(topicFromUrl) ? topicFromUrl : ''));
  const [flightRef, setFlightRef] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaCode, setCaptchaCode] = useState(() => randomCaptcha());
  const [status, setStatus] = useState({ type: '', text: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = 'Contact us | Nouakchott Oumtounsy International Airport (NKC/GQNO)';
  }, []);

  useEffect(() => {
    if (isValidContactTopic(topicFromUrl)) {
      setTopic(topicFromUrl);
    }
  }, [topicFromUrl]);

  const showFlightRef = topic === 'flights' || topic === 'baggage';

  const topicHint = useMemo(() => {
    switch (topic) {
      case 'flights':
        return 'For live times, also check the flight board. Airline ticket changes are handled by your carrier.';
      case 'baggage':
        return 'Include flight number, date, and bag tag if you have them. Airline desks handle most baggage claims.';
      case 'cargo':
        return 'Share your air waybill (AWB) number when enquiring about a shipment.';
      case 'vip':
        return 'VIP bookings usually need at least 24 hours’ notice with flight details and passenger count.';
      case 'careers':
        return 'Attach role title of interest in your message. We do not process unsolicited large attachments here.';
      case 'commercial':
        return 'Tell us about retail, advertising, or partnership opportunities you have in mind.';
      case 'privacy':
        return 'Privacy inquiries go to the IT Department, Ahmed Lemrabott, IT Manager (Ahmed.Lemrabott@Afroportnkc.com).';
      default:
        return '';
    }
  }, [topic]);

  const refreshCaptcha = () => {
    setCaptchaCode(randomCaptcha());
    setCaptchaInput('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', text: '' });

    if (!topic) {
      setStatus({ type: 'error', text: 'Please select the reason for your enquiry.' });
      return;
    }
    if (email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) {
      setStatus({ type: 'error', text: 'E-mail addresses do not match. Please check and try again.' });
      return;
    }
    if (captchaInput.trim().toLowerCase() !== captchaCode.toLowerCase()) {
      setStatus({ type: 'error', text: 'Captcha does not match. Please try again.' });
      refreshCaptcha();
      return;
    }

    setSending(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          topic,
          flightRef,
          subject,
          message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus({ type: 'error', text: data?.error || 'Your message could not be sent. Please try again.' });
        refreshCaptcha();
        return;
      }
      setStatus({
        type: 'ok',
        text: 'Thank you. Your enquiry has been sent successfully.',
      });
      setName('');
      setEmail('');
      setEmailConfirm('');
      setPhone('');
      setTopic(isValidContactTopic(topicFromUrl) ? topicFromUrl : '');
      setFlightRef('');
      setSubject('');
      setMessage('');
      refreshCaptcha();
    } catch {
      setStatus({
        type: 'error',
        text: 'Unable to send your enquiry right now. Please try again shortly.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <InnerPage
      slug="contact"
      heading="Contact us"
      lead="We're here to assist you. Email, call, or send an enquiry to Nouakchott Oumtounsy International Airport (NKC/GQNO)."
      banner="/images/airport/airport-exterior-view.jpg"
      section="services"
    >
      <section className="section inner-content contact-section">
        <div className="container contact-layout">
          <div className="contact-intro">
            <p className="inner-lead">
              We&apos;re here to assist you. Email or call Nouakchott Oumtounsy International Airport
              (NKC/GQNO), or use the form below to send your enquiry.
            </p>
            <p className="contact-intro__note">
              Choose the topic that best matches your enquiry so we can route it to the right airport
              team. Phone lines:
            </p>
            <div className="contact-channels">
              <p className="contact-hours">
                Opening hours: <strong>24/7</strong> (
                <abbr title="Greenwich Mean Time">GMT</abbr>)
              </p>
              <ul className="contact-phone-list">
                <li>
                  <a href="tel:+22242424944" className="contact-channel contact-channel--phone">
                    <span className="contact-channel__icon" aria-hidden="true">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </span>
                    <span className="contact-channel__body">
                      <span className="contact-channel__label">Baggage</span>
                      <span className="contact-channel__value">(+222) 42 42 49 44</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href="tel:+22242420252" className="contact-channel contact-channel--phone">
                    <span className="contact-channel__icon" aria-hidden="true">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </span>
                    <span className="contact-channel__body">
                      <span className="contact-channel__label">Passage</span>
                      <span className="contact-channel__value">(+222) 42 42 02 52</span>
                    </span>
                  </a>
                </li>
              </ul>
              <a href="mailto:info@afroportnkc.com" className="contact-channel contact-channel--email">
                <span className="contact-channel__icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <path d="m22 6-10 7L2 6" />
                  </svg>
                </span>
                <span className="contact-channel__body">
                  <span className="contact-channel__label">Official email</span>
                  <span className="contact-channel__value contact-channel__value--email">info@afroportnkc.com</span>
                  <span className="contact-channel__note">
                    General enquiries, lost property, commercial, and media
                  </span>
                </span>
              </a>
              <div className="contact-channel contact-channel--address">
                <span className="contact-channel__icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <div className="contact-channel__body">
                  <p className="contact-channel__label">Postal address</p>
                  <address className="contact-channel__value contact-channel__value--address">
                    Afroport Mauritanie SARL
                    <br />
                    B.P 7117, Nouakchott, Mauritania
                  </address>
                </div>
              </div>
            </div>
          </div>

          <form className="contact-form" id="contactForm" onSubmit={onSubmit} noValidate>
            <div className="contact-form__stack">
              <label className="contact-form__label" htmlFor="contactTopic">
                Enquiry type <span className="contact-form__req">*</span>
              </label>
              <div className="contact-form__field contact-form__field--select">
                <FieldIcon>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 6h16M4 12h10M4 18h14" />
                  </svg>
                </FieldIcon>
                <select
                  id="contactTopic"
                  name="topic"
                  className="contact-form__input contact-form__select"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                  aria-describedby={topicHint ? 'contactTopicHint' : undefined}
                >
                  {CONTACT_TOPICS.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value} disabled={opt.value === ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {topicHint ? (
                <p id="contactTopicHint" className="contact-form__hint contact-form__hint--topic">
                  {topicHint}
                </p>
              ) : null}
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label visually-hidden" htmlFor="contactName">
                Your name / first name
              </label>
              <FieldIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </FieldIcon>
              <input
                type="text"
                id="contactName"
                name="name"
                className="contact-form__input"
                placeholder="Your name / first name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label visually-hidden" htmlFor="contactEmail">
                Your e-mail address
              </label>
              <FieldIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <path d="m22 6-10 7L2 6" />
                </svg>
              </FieldIcon>
              <input
                type="email"
                id="contactEmail"
                name="email"
                className="contact-form__input"
                placeholder="Your e-mail address"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label visually-hidden" htmlFor="contactEmailConfirm">
                Rewrite your e-mail address
              </label>
              <FieldIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <path d="m22 6-10 7L2 6" />
                </svg>
              </FieldIcon>
              <input
                type="email"
                id="contactEmailConfirm"
                name="email_confirm"
                className="contact-form__input"
                placeholder="Rewrite your e-mail address"
                autoComplete="email"
                required
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
              />
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label visually-hidden" htmlFor="contactPhone">
                Phone
              </label>
              <FieldIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </FieldIcon>
              <input
                type="tel"
                id="contactPhone"
                name="phone"
                className="contact-form__input"
                placeholder="Phone (optional)"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            {showFlightRef ? (
              <div className="contact-form__field">
                <label className="contact-form__label visually-hidden" htmlFor="contactFlight">
                  Flight number or date
                </label>
                <FieldIcon>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.18 9 2 14.5l1.5.75L10 12l7.5 8.5 1.5-.75L12 10.5 21 7l-.75-1.5L10.18 9Z" />
                  </svg>
                </FieldIcon>
                <input
                  type="text"
                  id="contactFlight"
                  name="flight_ref"
                  className="contact-form__input"
                  placeholder="Flight number / travel date (optional)"
                  autoComplete="off"
                  value={flightRef}
                  onChange={(e) => setFlightRef(e.target.value)}
                />
              </div>
            ) : null}

            <div className="contact-form__field">
              <label className="contact-form__label visually-hidden" htmlFor="contactSubject">
                Subject of your message
              </label>
              <FieldIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </FieldIcon>
              <input
                type="text"
                id="contactSubject"
                name="subject"
                className="contact-form__input"
                placeholder="Subject of your message"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="contact-form__field contact-form__field--textarea">
              <label className="contact-form__label visually-hidden" htmlFor="contactMessage">
                Your message
              </label>
              <FieldIcon top>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
              </FieldIcon>
              <textarea
                id="contactMessage"
                name="message"
                className="contact-form__input contact-form__textarea"
                rows={6}
                placeholder="Your message. Include useful details such as dates, flight, and location in the terminal."
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div className="contact-form__captcha">
              <button
                type="button"
                className="contact-form__captcha-image"
                id="contactCaptchaImage"
                onClick={refreshCaptcha}
                title="Click to refresh captcha"
                aria-label="Refresh captcha code"
              >
                {captchaCode}
              </button>
              <div className="contact-form__field contact-form__field--captcha">
                <label className="contact-form__label visually-hidden" htmlFor="contactCaptcha">
                  Captcha security
                </label>
                <FieldIcon>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </FieldIcon>
                <input
                  type="text"
                  id="contactCaptcha"
                  name="captcha"
                  className="contact-form__input"
                  placeholder="Captcha security"
                  autoComplete="off"
                  required
                  aria-describedby="contactCaptchaHint"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                />
              </div>
            </div>
            <p id="contactCaptchaHint" className="contact-form__hint">
              Enter the characters shown. Click the code to refresh.
            </p>

            <button type="submit" className="btn btn--accent contact-form__submit" disabled={sending}>
              {sending ? 'Sending...' : 'Send enquiry'}
            </button>

            {status.text ? (
              <p
                className={`contact-form__status${status.type === 'error' ? ' contact-form__status--error' : ' contact-form__status--ok'}`}
                role="status"
                aria-live="polite"
              >
                {status.text}
              </p>
            ) : null}
          </form>

          <p className="inner-disclaimer contact-disclaimer" role="note">
            Airport and flight information on this website is provided for general guidance only and
            may change without notice. While Nouakchott Oumtounsy International Airport (NKC/GQNO)
            strives to keep content accurate and up to date, information may occasionally be
            incomplete, outdated, or incorrect. Always confirm flight schedules, check-in times,
            baggage allowances, facilities, services, and other travel requirements with your airline
            or the relevant authority before travelling. Entry, visa, customs, and health requirements
            should be verified with the relevant government authorities. Where flight times are shown,
            they are in <abbr title="Greenwich Mean Time">GMT</abbr> (Nouakchott).
          </p>
        </div>
      </section>
    </InnerPage>
  );
}
